
import { useRef, useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToast,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react'
import { Search, Plus, Download, Trash2, RotateCcw, Database, File, HardDrive } from 'lucide-react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { BackupService } from '@/lib/services/backupService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { Backup } from '@/types/backup'
import { formatDistanceToNow } from 'date-fns'

export default function BackupRestorePage() {
  const colors = useThemeColors()
  const warningColor = useColorModeValue('orange.600', 'orange.400')
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const toast = useToast()

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedType, setSelectedType] = useState<string>('')
  const [selectedStatus, setSelectedStatus] = useState<string>('')
  // Default stats object - never null/undefined
  const defaultStats = {
    total: 0,
    completed: 0,
    failed: 0,
    total_size: 0,
  }

  const [stats, setStats] = useState<{
    total: number
    completed: number
    failed: number
    total_size: number
    pending?: number
    in_progress?: number
    database_backups?: number
    files_backups?: number
    full_backups?: number
  }>(defaultStats)
  const [isLoadingStats, setIsLoadingStats] = useState(false)
  const [restoreBackup, setRestoreBackup] = useState<Backup | null>(null)
  const { isOpen: isRestoreOpen, onOpen: onRestoreOpen, onClose: onRestoreClose } = useDisclosure()
  const [isRestoring, setIsRestoring] = useState(false)

  // Fetch statistics
  useEffect(() => {
    const fetchStats = async () => {
      setIsLoadingStats(true)
      try {
        const statsData = await BackupService.getBackupStats()
        // Always set stats, even if undefined - use defaults
        setStats(statsData || defaultStats)
      } catch (error: any) {
        // Service now returns default values, but log error if it still throws
        console.error('Error fetching stats:', error)
        showError(error)
        // Ensure stats is never null/undefined
        setStats(defaultStats)
      } finally {
        setIsLoadingStats(false)
      }
    }
    fetchStats()
  }, [showError])

  // Type filter options
  const typeFilterOptions = useMemo(() => [
    { value: 'database', label: 'Database' },
    { value: 'files', label: 'Files' },
    { value: 'full', label: 'Full' },
  ], [])

  // Status filter options
  const statusFilterOptions = useMemo(() => [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' },
  ], [])

  const tableOptions: DataTableOptions = useMemo(
    () => {
      const formatFileSizeForTable = (bytes: number | null): string => {
        if (!bytes) return '0 B'
        const units = ['B', 'KB', 'MB', 'GB', 'TB']
        let size = bytes
        let unit = 0
        while (size >= 1024 && unit < units.length - 1) {
          size /= 1024
          unit++
        }
        return `${size.toFixed(2)} ${units[unit]}`
      }

      return {
      dataFields: {
        name: {
          name: 'Name',
          withSort: true,
          maxWidth: '200px',
        },
        type: {
          name: 'Type',
          withSort: true,
          withFilter: true,
          maxWidth: '120px',
          badge: {
            database: 'blue',
            files: 'green',
            full: 'purple',
          },
          badgeLabels: {
            database: 'Database',
            files: 'Files',
            full: 'Full',
          },
        },
        status: {
          name: 'Status',
          withSort: true,
          withFilter: true,
          maxWidth: '140px',
          badge: {
            pending: 'yellow',
            in_progress: 'blue',
            completed: 'green',
            failed: 'red',
            cancelled: 'gray',
          },
          badgeLabels: {
            pending: 'Pending',
            in_progress: 'In Progress',
            completed: 'Completed',
            failed: 'Failed',
            cancelled: 'Cancelled',
          },
        },
        file_size: {
          name: 'Size',
          withSort: true,
          maxWidth: '100px',
        },
        created_at: {
          name: 'Created',
          withSort: true,
          maxWidth: '180px',
        },
      },
      dataSource: '/backups',
      primaryKey: 'id',
      softDelete: false,
      displayInModal: false,
      noRecordText: 'No backups found',
      otherActions: [
        {
          name: 'Download',
          onClick: async (row: Backup) => {
            try {
              const blob = await BackupService.downloadBackup(row.id)
              const url = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = `${row.name}.${row.compression === 'gzip' ? 'gz' : row.compression === 'zip' ? 'zip' : 'sql'}`
              document.body.appendChild(a)
              a.click()
              window.URL.revokeObjectURL(url)
              document.body.removeChild(a)
              showSuccess('Backup downloaded successfully')
            } catch (error: any) {
              showError(error)
            }
          },
          show: (row: Backup) => row.status === 'completed' && row.file_path !== null,
        },
        {
          name: 'Restore',
          onClick: (row: Backup) => {
            setRestoreBackup(row)
            onRestoreOpen()
          },
          show: (row: Backup) => row.status === 'completed' && row.file_path !== null,
        },
      ],
      }
    },
    [onRestoreOpen, showError, showSuccess]
  )

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this backup?')) {
      return
    }

    try {
      await BackupService.deleteBackup(id)
      showSuccess('Backup deleted successfully')
      tableRef.current?.reload()
      // Refresh stats
      try {
        const statsData = await BackupService.getBackupStats()
        // Always set stats, even if undefined - use defaults
        setStats(statsData || defaultStats)
      } catch (statsError) {
        // Silently fail stats refresh - ensure stats is never null/undefined
        console.error('Failed to refresh stats:', statsError)
        setStats(defaultStats)
      }
    } catch (error: any) {
      showError(error)
    }
  }

  const handleRestore = async () => {
    if (!restoreBackup) return

    setIsRestoring(true)
    try {
      await BackupService.restoreBackup(restoreBackup.id, {
        confirm: true,
        create_backup: true,
      })
      showSuccess('Backup restored successfully')
      onRestoreClose()
      setRestoreBackup(null)
      tableRef.current?.reload()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsRestoring(false)
    }
  }

  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let size = bytes
    let unit = 0
    while (size >= 1024 && unit < units.length - 1) {
      size /= 1024
      unit++
    }
    return `${size.toFixed(2)} ${units[unit]}`
  }

  const searchRef = useRef<HTMLInputElement>(null)

  const handleSearch = () => {
    const query = searchRef.current?.value || ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <AdminLayout>
        <Box
          p={4}
          h="100%"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
            {/* Statistics Cards */}
            {isLoadingStats ? (
              <Card flexShrink={0}>
                <CardBody py={3}>
                  <Spinner />
                </CardBody>
              </Card>
            ) : (
              <HStack spacing={3} flexShrink={0}>
                <Card flex={1}>
                  <CardBody py={3}>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" color="gray.500">Total Backups</Text>
                      <Text fontSize="2xl" fontWeight="bold">{stats?.total ?? 0}</Text>
                    </VStack>
                  </CardBody>
                </Card>
                <Card flex={1}>
                  <CardBody py={3}>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" color="gray.500">Completed</Text>
                      <Text fontSize="2xl" fontWeight="bold" color="green.500">{stats?.completed ?? 0}</Text>
                    </VStack>
                  </CardBody>
                </Card>
                <Card flex={1}>
                  <CardBody py={3}>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" color="gray.500">Failed</Text>
                      <Text fontSize="2xl" fontWeight="bold" color="red.500">{stats?.failed ?? 0}</Text>
                    </VStack>
                  </CardBody>
                </Card>
                <Card flex={1}>
                  <CardBody py={3}>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" color="gray.500">Total Size</Text>
                      <Text fontSize="2xl" fontWeight="bold">{formatFileSize(stats?.total_size ?? 0)}</Text>
                    </VStack>
                  </CardBody>
                </Card>
              </HStack>
            )}

            {/* Main Card */}
            <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
              <CardHeader flexShrink={0} py={3}>
                <Heading size="md" mb={1}>Backup & Restore</Heading>
                <Text fontSize="sm" color={colors.text}>
                  Manage system backups and restore points
                </Text>
              </CardHeader>

              {/* Filters, Search and Action Buttons */}
              <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
                <HStack spacing={4} flexWrap="nowrap">
                  <Box flex="1" minW="200px">
                    <InputGroup size="sm">
                      <Input
                        ref={searchRef}
                        placeholder="Search backups..."
                        onKeyDown={handleKeyDown}
                        defaultValue={searchQuery}
                        pr="2.5rem"
                      />
                      <InputRightElement width="2.5rem">
                        <IconButton
                          aria-label="Search"
                          icon={<Search size={16} />}
                          size="sm"
                          h="1.75rem"
                          w="1.75rem"
                          onClick={handleSearch}
                          variant="ghost"
                        />
                      </InputRightElement>
                    </InputGroup>
                  </Box>
                  <Select
                    placeholder="All Types"
                    size="sm"
                    value={selectedType}
                    onChange={(e) => {
                      e.stopPropagation()
                      setSelectedType(e.target.value)
                      tableRef.current?.applyFilter('type', e.target.value || undefined)
                    }}
                    flex="0 0 auto"
                    width="150px"
                  >
                    <option value="">All Types</option>
                    {typeFilterOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  <Select
                    placeholder="All Status"
                    size="sm"
                    value={selectedStatus}
                    onChange={(e) => {
                      e.stopPropagation()
                      setSelectedStatus(e.target.value)
                      tableRef.current?.applyFilter('status', e.target.value || undefined)
                    }}
                    flex="0 0 auto"
                    width="150px"
                  >
                    <option value="">All Status</option>
                    {statusFilterOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  <HStack spacing={2} flex="0 0 auto">
                    <Button
                      leftIcon={<Plus size={18} />}
                      colorScheme="blue"
                      size="sm"
                      onClick={() => navigate('/system-settings/backup-restore/create')}
                    >
                      Create Backup
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/system-settings/backup-restore/schedules')}
                    >
                      Manage Schedules
                    </Button>
                  </HStack>
                </HStack>
              </CardHeader>

              {/* Table */}
            <CardBody 
                p={0} 
                pt={1}
                pb={4}
                mx={5}
                flex="1" 
                overflow="hidden" 
                display="flex" 
                flexDirection="column" 
                minH={0}
                position="relative"
              >
                <Box h="100%" display="flex" flexDirection="column" minH={0}>
                  <DataTable
                    ref={tableRef}
                    options={tableOptions}
                    params={useMemo(
                      () => ({
                        search: searchQuery || undefined,
                        type: selectedType || undefined,
                        status: selectedStatus || undefined,
                      }),
                      [searchQuery, selectedType, selectedStatus]
                    )}
                    access={{
                      can_edit: false,
                      can_delete: true,
                    }}
                    filterOptions={{
                      type: typeFilterOptions,
                      status: statusFilterOptions,
                    }}
                  />
                </Box>
              </CardBody>
            </Card>
          </VStack>
        </Box>

        {/* Restore Confirmation Modal */}
        <Modal isOpen={isRestoreOpen} onClose={onRestoreClose} size="md">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Restore Backup</ModalHeader>
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Text color={colors.text}>
                  Are you sure you want to restore this backup? This will replace your current data.
                </Text>
                {restoreBackup && (
                  <Box p={4} bg={colors.sectionBg} borderRadius="md" borderWidth="1px" borderColor={colors.border}>
                    <Text fontWeight="bold" mb={2} color={colors.heading}>Backup Details:</Text>
                    <Text color={colors.text}><strong style={{ color: colors.heading }}>Name:</strong> {restoreBackup.name}</Text>
                    <Text color={colors.text}><strong style={{ color: colors.heading }}>Type:</strong> {restoreBackup.type}</Text>
                    <Text color={colors.text}><strong style={{ color: colors.heading }}>Created:</strong> {formatDistanceToNow(new Date(restoreBackup.created_at), { addSuffix: true })}</Text>
                  </Box>
                )}
                <Text fontSize="sm" color={warningColor}>
                  ⚠️ A backup will be created automatically before restore for safety.
                </Text>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onRestoreClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleRestore}
                isLoading={isRestoring}
                loadingText="Restoring..."
              >
                Confirm Restore
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </AdminLayout>
  )
}

